import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Close from '@mui/icons-material/Close';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import CloseIcon from '@mui/icons-material/Close';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import {
	AddFaq,
	InformationQaOverviewDTO,
} from '@/api/DTO/information/faq.interface';
import {
	useCreateFaqMutation,
	useDeleteFaqMutation,
	useUpdateFaqMutation,
} from '@/api/information/faqApiSlice';
import { FormType } from '@/api/enum/formType.enum';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';

interface Props {
	type: FormType;
	row?: InformationQaOverviewDTO;
}

export default function AddPageDialog(props: Props) {
	const { type, row } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [createFaq] = useCreateFaqMutation();
	const [updateFaq] = useUpdateFaqMutation();
	const [deleteFaq] = useDeleteFaqMutation();
	const showModal = useModal();

	const defaultValues: DefaultValues<AddFaq> = row ? row : {};

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
	});

	useEffect(() => {
		if (row) {
			methods.reset(row);
		}
	}, [row]);

	const methods = useForm<AddFaq>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset();
		setOpen(false);
	};

	const onSubmit: SubmitHandler<AddFaq> = async (data) => {
		const response =
			type == FormType.create ? await createFaq(data) : await updateFaq(data);
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createPageSuccess')
					: t('snackbar.editPageSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			handleClose();
		}
	};

	const handleDelete = () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('faq.deletePage')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('faq.isDeletePage')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteFaq(row?.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deletePageSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
					}
				}}
			/>
		));
	};

	return (
		<>
			{type == FormType.create && (
				<Button
					endIcon={<DriveFileRenameOutlineOutlinedIcon />}
					onClick={() => {
						setOpen(true);
					}}
					sx={{ marginRight: '24px' }}
					variant='contained'
				>
					{t('faq.addPage')}
				</Button>
			)}
			{type == FormType.edit && (
				<IconButton
					color='primary'
					onClick={() => {
						setOpen(true);
					}}
				>
					<CreateOutlinedIcon />
				</IconButton>
			)}

			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle variant='h5'>
					{type == FormType.create ? t('faq.addPage') : t('faq.changePage')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='titleZh'
										label={t('faq.pageTitleZh')}
										maxLength={20}
									/>
								</Grid>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='titleEn'
										label={t('faq.pageTitleEn_')}
										maxLength={100}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					{type == FormType.edit && (
						<Button
							color='error'
							style={{ marginRight: 'auto' }}
							onClick={() => handleDelete()}
						>
							{t('button.delete')}
						</Button>
					)}
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.isConfirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
